import React, { useState } from 'react'
import useSWR from 'swr'
import type { TeamLite } from '../../../types'
import { fetcher } from '../../../swr-config'
import type { FormResponse, FormFixture, GoalscorerEvent } from '../types'
import { getLeagueBadge } from '../../../utils/leagueBadge'

interface MAFormTabProps {
  fixtureId: number
  homeTeam: TeamLite
  awayTeam: TeamLite
  onFixtureClick?: (fixtureId: number) => void
}

function formatGoalMinute(elapsed: number, elapsed_extra: number | null, isOwnGoal: boolean, isPenalty: boolean): string {
  const minute = elapsed_extra ? `${elapsed}+${elapsed_extra}'` : `${elapsed}'`
  if (isOwnGoal) return `OG ${minute}`
  if (isPenalty) return `P ${minute}`
  return minute
}

function formatScorers(scorers: GoalscorerEvent[]): string[] {
  // Group goals by player name
  const playerGoals = new Map<string, string[]>()

  scorers.forEach((g) => {
    const playerName = g.player_name || 'Unknown'
    const minute = formatGoalMinute(g.elapsed, g.elapsed_extra, g.is_own_goal, g.is_penalty)

    if (!playerGoals.has(playerName)) {
      playerGoals.set(playerName, [])
    }
    playerGoals.get(playerName)!.push(minute)
  })

  return Array.from(playerGoals.entries()).map(([name, minutes]) => {
    return `${name} ${minutes.join(', ')}`
  })
}

interface FormFixtureCardProps {
  fixture: FormFixture
  team: TeamLite  // The team whose form we're viewing
  onFixtureClick?: (fixtureId: number) => void
}

function FormFixtureCard({ fixture, team, onFixtureClick }: FormFixtureCardProps) {
  const handleClick = () => {
    if (onFixtureClick) {
      onFixtureClick(fixture.fixture_id)
    }
  }

  const resultColors = {
    W: { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' },
    D: { bg: 'rgba(156, 163, 175, 0.2)', color: '#9ca3af' },
    L: { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' },
  }

  const resultStyle = resultColors[fixture.result]
  const leagueBadge = getLeagueBadge(fixture.league_name)

  // Determine home/away team display
  const homeTeam = fixture.is_home ? team : fixture.opponent
  const awayTeam = fixture.is_home ? fixture.opponent : team
  const homeScore = fixture.is_home ? fixture.score_for : fixture.score_against
  const awayScore = fixture.is_home ? fixture.score_against : fixture.score_for

  return (
    <div className="form-fixture-card" onClick={handleClick}>
      {/* Header row with date, result badge, and league */}
      <div className="form-fixture-header">
        <span className="form-fixture-date">
          {new Date(fixture.date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
          })}
        </span>
        <span
          className="form-fixture-result"
          style={{ background: resultStyle.bg, color: resultStyle.color }}
        >
          {fixture.result}
        </span>
        <span
          className="form-fixture-league-badge"
          title={fixture.league_name || 'Unknown'}
          style={{
            background: leagueBadge.color,
            color: leagueBadge.color === '#dae025' ? '#000' : '#fff',
            fontSize: 9,
            fontWeight: 700,
            padding: '2px 5px',
            borderRadius: 3,
          }}
        >
          {leagueBadge.code}
        </span>
      </div>

      {/* Matchup row - Home vs Away */}
      <div className="form-fixture-matchup">
        <div className="form-fixture-team home">
          <span className="team-name">{homeTeam.name}</span>
          {homeTeam.crest_url && <img src={homeTeam.crest_url} alt="" className="team-crest" />}
        </div>
        <div className="form-fixture-center">
          <span className="form-fixture-score">{homeScore} - {awayScore}</span>
        </div>
        <div className="form-fixture-team away">
          {awayTeam.crest_url && <img src={awayTeam.crest_url} alt="" className="team-crest" />}
          <span className="team-name">{awayTeam.name}</span>
        </div>
      </div>

    </div>
  )
}

interface TeamFormSectionProps {
  team: TeamLite
  fixtures: FormFixture[]
  expanded: boolean
  onToggleExpand: () => void
  onFixtureClick?: (fixtureId: number) => void
}

function TeamFormSection({ team, fixtures, expanded, onToggleExpand, onFixtureClick }: TeamFormSectionProps) {
  const displayFixtures = expanded ? fixtures : fixtures.slice(0, 5)

  return (
    <div className="form-team">
      <div className="form-team-header">
        {team.crest_url && <img src={team.crest_url} alt="" />}
        <span className="team-name">{team.name}</span>
      </div>

      {displayFixtures.map((fixture) => (
        <FormFixtureCard
          key={fixture.fixture_id}
          fixture={fixture}
          team={team}
          onFixtureClick={onFixtureClick}
        />
      ))}

      {fixtures.length > 5 && (
        <button
          onClick={onToggleExpand}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            padding: '0.5rem',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            color: 'var(--muted)',
            fontSize: '0.75rem',
            cursor: 'pointer',
          }}
        >
          {expanded ? 'Show Less' : `Show ${fixtures.length - 5} More`}
        </button>
      )}
    </div>
  )
}

export function MAFormTab({ fixtureId, homeTeam, awayTeam, onFixtureClick }: MAFormTabProps) {
  const [homeExpanded, setHomeExpanded] = useState(false)
  const [awayExpanded, setAwayExpanded] = useState(false)
  const [mobileTeam, setMobileTeam] = useState<'home' | 'away'>('home')

  const { data, error } = useSWR<FormResponse>(
    `/fixtures/${fixtureId}/form`,
    fetcher,
    { revalidateOnFocus: false }
  )

  if (error) {
    return <div className="ma-error">Failed to load form data</div>
  }

  if (!data) {
    return <div className="ma-loading">Loading recent form...</div>
  }

  return (
    <div className="form-section">
      {/* Mobile team selector */}
      <div className="form-mobile-toggle">
        <button
          className={`form-team-toggle-btn ${mobileTeam === 'home' ? 'active' : ''}`}
          onClick={() => setMobileTeam('home')}
        >
          {homeTeam.crest_url && <img src={homeTeam.crest_url} alt="" />}
          <span>{homeTeam.name}</span>
        </button>
        <button
          className={`form-team-toggle-btn ${mobileTeam === 'away' ? 'active' : ''}`}
          onClick={() => setMobileTeam('away')}
        >
          {awayTeam.crest_url && <img src={awayTeam.crest_url} alt="" />}
          <span>{awayTeam.name}</span>
        </button>
      </div>

      <div className={`form-team-wrapper ${mobileTeam === 'home' ? 'mobile-visible' : 'mobile-hidden'}`}>
        <TeamFormSection
          team={homeTeam}
          fixtures={data.home_team.fixtures}
          expanded={homeExpanded}
          onToggleExpand={() => setHomeExpanded(!homeExpanded)}
          onFixtureClick={onFixtureClick}
        />
      </div>
      <div className={`form-team-wrapper ${mobileTeam === 'away' ? 'mobile-visible' : 'mobile-hidden'}`}>
        <TeamFormSection
          team={awayTeam}
          fixtures={data.away_team.fixtures}
          expanded={awayExpanded}
          onToggleExpand={() => setAwayExpanded(!awayExpanded)}
          onFixtureClick={onFixtureClick}
        />
      </div>
    </div>
  )
}
